import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import BottomNav from './BottomNav';
import { 
  TrendingUp, 
  Eye, 
  EyeOff, 
  Plus, 
  Target, 
  Activity as ActivityIcon,
  Bell,
  Settings,
  Home,
  PieChart as PieChartIcon,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCw
} from 'lucide-react';

// Add animations
const styles = document.createElement('style');
styles.innerHTML = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
if (!document.head.querySelector('#dashboard-animations')) {
  styles.id = 'dashboard-animations';
  document.head.appendChild(styles);
}

// Design Constants matching previous fork's aesthetic
const COLORS = {
  beige: '#F5F3ED',
  navy: '#1a1a3e',
  orange: '#FF8C42',
  orangeLight: '#FFB366',
  white: '#ffffff',
  gray: {
    100: '#f5f7f8',
    200: '#e8ecee',
    300: '#c3cacf',
    400: '#9ba4ab',
    500: '#6b7280',
    600: '#4c5b62'
  },
  success: '#10b981',
  error: '#ef4444'
};

// Mock user data (financial data - to be replaced with real API calls later)
const mockUserFinancials = {
  netWorth: 1625000,
  monthlyChange: 84500,
  changePercent: 5.5
};

// Mock Goals Data - keep dollar amounts consistent
const mockGoals = [
  {
    id: '1',
    name: 'Beach House',
    description: 'Beachfront property with pool',
    targetAmount: 2250000,
    currentAmount: 450000,
    targetDate: '2035-03-01',
    portfolio: 'Horizon',
    portfolioReturn: '+11.3%',
    progress: 20,
    image: 'https://customer-assets.emergentagent.com/job_asset-harmony-1/artifacts/767a8grf_image.webp'
  },
  {
    id: '2',
    name: 'Financial Freedom',
    description: 'Early retirement fund',
    targetAmount: 3500000,
    currentAmount: 750000,
    targetDate: '2040-01-01',
    portfolio: 'Velocity',
    portfolioReturn: '+13.8%',
    progress: 21,
    image: 'https://customer-assets.emergentagent.com/job_asset-harmony-1/artifacts/ex57r7mr_nick-fewings-GpplHQ-fyvA-unsplash.jpg'
  },
  {
    id: '3',
    name: "Children's Education",
    description: 'Future education fund',
    targetAmount: 850000,
    currentAmount: 425000,
    targetDate: '2028-06-01',
    portfolio: 'Fortress',
    portfolioReturn: '+9.1%',
    progress: 50,
    image: 'https://customer-assets.emergentagent.com/job_asset-harmony-1/artifacts/bjmak8cv_note-thanun-CYlPykF-qAM-unsplash.jpg'
  }
];

// Mock Asset Allocation
const mockAssets = [
  { category: 'Equities', value: 568750, percentage: 35, color: '#3B82F6' },
  { category: 'Private Equity', value: 325000, percentage: 20, color: '#8B5CF6' },
  { category: 'Real Estate', value: 243750, percentage: 15, color: '#10B981' },
  { category: 'Private Credit', value: 195000, percentage: 12, color: '#F59E0B' },
  { category: 'Venture Capital', value: 162500, percentage: 10, color: '#EC4899' },
  { category: 'Infrastructure', value: 130000, percentage: 8, color: '#6366F1' }
];

// Mock Transactions
const mockTransactions = [
  { id: 1, type: 'deposit', description: 'Monthly Contribution', amount: 5000, date: '2025-01-15', goal: 'Financial Freedom' },
  { id: 2, type: 'dividend', description: 'Quarterly Dividend', amount: 2450, date: '2025-01-10', goal: 'Beach House' },
  { id: 3, type: 'rebalance', description: 'Portfolio Rebalancing', amount: 0, date: '2025-01-08', goal: null },
  { id: 4, type: 'deposit', description: 'One-time Deposit', amount: 15000, date: '2025-01-05', goal: "Children's Education" }
];

const HollowDonutChart = ({ data, total, size = 350 }) => {
  const strokeWidth = size * 0.12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const centerX = size / 2;
  const centerY = size / 2;
  
  let cumulativePercent = 0;
  
  const getLabelPosition = (startPercent, percent) => {
    const midPercent = startPercent + percent / 2;
    const angle = (midPercent / 100) * 2 * Math.PI - Math.PI / 2;
    const labelRadius = radius;
    return {
      x: centerX + labelRadius * Math.cos(angle),
      y: centerY + labelRadius * Math.sin(angle)
    };
  };
  
  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
      <svg width={size} height={size}>
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke={COLORS.gray[200]}
          strokeWidth={strokeWidth}
        />
        
        <g transform={`rotate(-90 ${centerX} ${centerY})`}>
          {data.map((item, index) => {
            const percent = item.percentage;
            const offset = (cumulativePercent / 100) * circumference;
            const dashArray = `${(percent / 100) * circumference} ${circumference}`;
            const startPercent = cumulativePercent;
            cumulativePercent += percent;
            
            return (
              <circle
                key={index}
                cx={centerX}
                cy={centerY}
                r={radius}
                fill="none"
                stroke={item.color}
                strokeWidth={strokeWidth}
                strokeDasharray={dashArray}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                style={{ transition: 'all 0.3s ease' }}
              />
            );
          })}
        </g>
        
        {(() => {
          let cumulative = 0;
          return data.map((item, index) => {
            const startPercent = cumulative;
            const pos = getLabelPosition(startPercent, item.percentage);
            cumulative += item.percentage;
            
            return (
              <text
                key={`label-${index}`}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fill: 'white',
                  fontSize: `${size * 0.055}px`,
                  fontWeight: 'bold',
                  pointerEvents: 'none',
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}
              >
                {item.percentage}%
              </text>
            );
          });
        })()}
      </svg>
      
      {/* Center Amount */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: `${size * 0.08}px`,
          fontWeight: '600',
          color: COLORS.navy,
          fontFamily: 'Georgia, serif',
          lineHeight: 1
        }}>
          ${(total / 1000000).toFixed(2)}M
        </div>
        <div style={{
          fontSize: `${size * 0.04}px`,
          color: COLORS.gray[500],
          marginTop: '0.25rem'
        }}>
          Total Value
        </div>
      </div>
    </div>
  );
};

const EnhancedDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [activeTab, setActiveTab] = useState('home');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getTransactionIcon = (type) => {
    const iconProps = {
      size: 24,
      strokeWidth: 2,
      color: getTransactionColor(type)
    };
    
    switch(type) {
      case 'deposit': return <ArrowDownCircle {...iconProps} />;
      case 'dividend': return <DollarSign {...iconProps} />;
      case 'withdrawal': return <ArrowUpCircle {...iconProps} />;
      case 'rebalance': return <RefreshCw {...iconProps} />;
      default: return <TrendingUp {...iconProps} />;
    }
  };

  const getTransactionColor = (type) => {
    switch(type) {
      case 'deposit': return COLORS.success;
      case 'dividend': return COLORS.orange;
      case 'withdrawal': return COLORS.error;
      default: return COLORS.navy;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.beige, paddingTop: '80px' }}>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 pb-24">
        
        {/* Customer Name Section */}
        <div style={{ marginBottom: '2rem', paddingTop: '2rem' }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '300',
            color: COLORS.navy,
            fontFamily: 'Georgia, serif'
          }}>
            Welcome back, {user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Guest'}
          </h1>
        </div>

        {/* Net Worth Card */}
        <div style={{
          background: COLORS.white,
          borderRadius: '1.5rem',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}>
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span style={{ fontSize: '0.875rem', color: COLORS.gray[500], fontWeight: '500' }}>
                  Total Net Worth
                </span>
                <button 
                  onClick={() => setBalanceVisible(!balanceVisible)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    color: COLORS.gray[400]
                  }}
                >
                  {balanceVisible ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
              <div style={{
                fontSize: '3rem',
                fontWeight: '600',
                color: COLORS.navy,
                fontFamily: 'Georgia, serif',
                lineHeight: 1
              }}>
                {balanceVisible ? formatCurrency(mockUserFinancials.netWorth) : '••••••••'}
              </div>
            </div>
            <div style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '9999px',
              background: `${COLORS.success}15`,
              color: COLORS.success,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600'
            }}>
              <TrendingUp size={16} />
              +{mockUserFinancials.changePercent}%
            </div>
          </div>
          
          <div style={{ fontSize: '0.875rem', color: COLORS.gray[500] }}>
            <span style={{ color: COLORS.success, fontWeight: '600' }}>+{formatCurrency(mockUserFinancials.monthlyChange)}</span> this month
          </div>
        </div>

        {/* Goals Section */}
        <div style={{ marginBottom: '2rem' }}>
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '300',
                color: COLORS.navy,
                fontFamily: 'Georgia, serif'
              }}>
                Your Goals
              </h2>
              <button 
                onClick={() => navigate('/goals')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: COLORS.orange,
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                View All →
              </button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {mockGoals.map((goal) => (
                <div 
                  key={goal.id}
                  onClick={() => setSelectedGoal(goal)}
                  style={{
                    background: COLORS.white,
                    borderRadius: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  {/* Goal Image */}
                  <div style={{ position: 'relative', height: '160px', overflow: 'hidden' }}>
                    <img 
                      src={goal.image} 
                      alt={goal.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                      background: COLORS.orange,
                      color: COLORS.white,
                      padding: '0.5rem 1rem',
                      borderRadius: '9999px',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {goal.portfolio}
                    </div>
                  </div>

                  {/* Goal Info */}
                  <div style={{ padding: '1.25rem' }}>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: '300',
                      color: COLORS.navy,
                      marginBottom: '0.5rem',
                      fontFamily: 'Georgia, serif'
                    }}>
                      {goal.name}
                    </h3>
                  
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div className="flex justify-between mb-1">
                        <span style={{ fontSize: '0.75rem', color: COLORS.gray[500] }}>Progress</span>
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: COLORS.orange }}>
                          {goal.progress}%
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        background: COLORS.gray[200],
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${goal.progress}%`,
                          height: '100%',
                          background: COLORS.orange,
                          borderRadius: '3px'
                        }} />
                      </div>
                    </div>
                    
                    <div className="flex justify-between">
                      <div>
                        <div style={{ fontSize: '0.75rem', color: COLORS.gray[500] }}>Current</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: COLORS.navy }}>
                          {formatCurrency(goal.currentAmount)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.75rem', color: COLORS.gray[500] }}>Target</div>
                        <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: COLORS.navy }}>
                          {formatCurrency(goal.targetAmount)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Asset Overview */}
        <div style={{ marginBottom: '2rem' }}>
          <div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '300',
              color: COLORS.navy,
              marginBottom: '1.5rem',
              fontFamily: 'Georgia, serif'
            }}>
              Asset Overview
            </h2>
            <div style={{
              background: COLORS.white,
              borderRadius: '1.5rem',
              padding: '2rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <HollowDonutChart data={mockAssets} total={mockUserFinancials.netWorth} size={350} />
              
              {/* Legend - 2 columns */}
              <div style={{ 
                marginTop: '2rem',
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.75rem'
              }}>
                {mockAssets.map((asset, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem'
                  }}>
                    <div style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '3px',
                      background: asset.color,
                      flexShrink: 0
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '0.8125rem', 
                        color: COLORS.gray[600],
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {asset.category}
                      </div>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: COLORS.gray[400]
                      }}>
                        {asset.percentage}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div style={{ marginBottom: '2rem' }}>
          <div className="flex justify-between items-center mb-4">
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '300',
              color: COLORS.navy,
              fontFamily: 'Georgia, serif'
            }}>
              Recent Activity
            </h2>
            <button 
              onClick={() => navigate('/activity')}
              style={{
                background: 'none',
                border: 'none',
                color: COLORS.orange,
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              View All →
            </button>
          </div>
          <div style={{
            background: COLORS.white,
            borderRadius: '1rem',
            padding: '1rem'
          }}>
            {mockTransactions.map((tx, index) => (
              <div key={tx.id} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '1rem',
                borderBottom: index < mockTransactions.length - 1 ? `1px solid ${COLORS.gray[200]}` : 'none'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: COLORS.gray[100],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '1rem'
                }}>
                  {getTransactionIcon(tx.type)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: COLORS.navy, marginBottom: '0.25rem' }}>
                    {tx.description}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: COLORS.gray[500] }}>
                    {new Date(tx.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  {tx.goal && (
                    <div style={{
                      display: 'inline-block',
                      marginTop: '0.25rem',
                      padding: '0.125rem 0.5rem',
                      background: COLORS.gray[100],
                      borderRadius: '0.5rem',
                      fontSize: '0.75rem',
                      color: COLORS.gray[500]
                    }}>
                      {tx.goal}
                    </div>
                  )}
                </div>
                {tx.amount !== 0 && (
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: getTransactionColor(tx.type)
                  }}>
                    {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <BottomNav />

      {/* Goal Detail Modal */}
      {selectedGoal && (
        <div 
          onClick={() => setSelectedGoal(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '2rem 1rem',
            zIndex: 50,
            animation: 'fadeIn 0.2s ease-in',
            overflowY: 'auto'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: COLORS.white,
              borderRadius: '1.5rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: 'none',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              animation: 'slideUp 0.3s ease-out',
              marginBottom: '2rem'
            }}
          >
            {/* Goal Image */}
            <div style={{ position: 'relative', height: '240px', overflow: 'hidden' }}>
              <img 
                src={selectedGoal.image} 
                alt={selectedGoal.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <button
                onClick={() => setSelectedGoal(null)}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: COLORS.white,
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  fontSize: '1.25rem',
                  color: COLORS.gray[600]
                }}
              >
                ×
              </button>
            </div>

            {/* Goal Details */}
            <div style={{ padding: '2rem' }}>
              <div style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '1rem',
                background: `${COLORS.orange}20`,
                color: COLORS.orange,
                fontSize: '0.75rem',
                fontWeight: '600',
                marginBottom: '1rem'
              }}>
                {selectedGoal.portfolio} Portfolio
              </div>
              
              <h2 style={{
                fontSize: '2rem',
                fontWeight: '300',
                color: COLORS.navy,
                marginBottom: '0.5rem',
                fontFamily: 'Georgia, serif'
              }}>
                {selectedGoal.name}
              </h2>
              
              <p style={{
                fontSize: '1rem',
                color: COLORS.gray[500],
                marginBottom: '2rem'
              }}>
                {selectedGoal.description}
              </p>

              {/* Stats Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '1rem',
                marginBottom: '2rem',
                padding: '1.5rem',
                background: COLORS.gray[100],
                borderRadius: '1rem'
              }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.25rem' }}>
                    Current Balance
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: COLORS.navy, fontFamily: 'Georgia, serif' }}>
                    {formatCurrency(selectedGoal.currentAmount)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.25rem' }}>
                    Target Amount
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: COLORS.navy, fontFamily: 'Georgia, serif' }}>
                    {formatCurrency(selectedGoal.targetAmount)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.25rem' }}>
                    Portfolio Return
                  </div>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    color: COLORS.success,
                    fontFamily: 'Georgia, serif',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    <TrendingUp size={20} />
                    {selectedGoal.portfolioReturn}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.25rem' }}>
                    Target Date
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600', color: COLORS.navy, fontFamily: 'Georgia, serif' }}>
                    {new Date(selectedGoal.targetDate).getFullYear()}
                  </div>
                </div>
              </div>

              {/* Progress Section */}
              <div style={{ marginBottom: '2rem' }}>
                <div className="flex justify-between mb-3">
                  <span style={{ fontSize: '1rem', fontWeight: '600', color: COLORS.navy }}>
                    Progress to Goal
                  </span>
                  <span style={{ fontSize: '1rem', fontWeight: '600', color: COLORS.orange }}>
                    {selectedGoal.progress}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '12px',
                  background: COLORS.gray[200],
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${selectedGoal.progress}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, ${COLORS.orange} 0%, ${COLORS.orangeLight} 100%)`,
                    borderRadius: '6px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button style={{
                  flex: 1,
                  padding: '1rem',
                  background: COLORS.orange,
                  border: 'none',
                  borderRadius: '0.75rem',
                  color: COLORS.white,
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = COLORS.orangeLight}
                onMouseOut={(e) => e.currentTarget.style.background = COLORS.orange}
                >
                  Deposit
                </button>
                <button style={{
                  flex: 1,
                  padding: '1rem',
                  background: 'transparent',
                  border: `2px solid ${COLORS.gray[300]}`,
                  borderRadius: '0.75rem',
                  color: COLORS.navy,
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = COLORS.orange;
                  e.currentTarget.style.color = COLORS.orange;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = COLORS.gray[300];
                  e.currentTarget.style.color = COLORS.navy;
                }}
                onClick={() => {
                  setSelectedGoal(null);
                  navigate(`/goal/${selectedGoal.id}`);
                }}
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedDashboard;